"use client";

import type { SbBlokData } from "@storyblok/react/rsc";
import { storyblokEditable } from "@/lib/storyblok-editable";

import { AssetImage } from "@/components/optilase/asset-image";

import { sectionSpacing } from "@/components/optilase/section-spacing";
import { headingOn, onSurface, surfaceFrom } from "@/components/optilase/surface";
import { cn } from "@/lib/utils";

type ChecklistGridBlok = SbBlokData & {
  heading?: string;
  intro?: string;
  items?: string;
  columns?: string;
  marker?: string;
  footnote?: string;
  image?: { filename?: string; alt?: string; focus?: string };
  image_position?: string;
  spacing?: string;
  bg_color?: unknown;
  card_color?: unknown;
};

function Tick() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="size-5 shrink-0" aria-hidden>
      <circle cx="10" cy="10" r="10" fill="currentColor" />
      <path
        d="M5.8 10.3 8.6 13l5.6-6"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Dot() {
  return (
    <svg viewBox="0 0 20 20" fill="none" className="size-5 shrink-0" aria-hidden>
      <circle cx="10" cy="10" r="9" stroke="currentColor" strokeWidth="1.6" />
      <circle cx="10" cy="10" r="3.2" fill="currentColor" />
    </svg>
  );
}

export default function ChecklistGrid({ blok }: { blok: ChecklistGridBlok }) {
  const surface = surfaceFrom(blok.bg_color);
  const card = surfaceFrom(blok.card_color);
  const items = (blok.items || "")
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  if (items.length === 0) return null;

  const image = blok.image?.filename || "";
  const imageLeft = blok.image_position === "left";
  const cols = image
    ? "sm:grid-cols-2"
    : blok.columns === "two"
      ? "sm:grid-cols-2"
      : blok.columns === "four"
        ? "sm:grid-cols-2 lg:grid-cols-4"
        : "sm:grid-cols-2 lg:grid-cols-3";
  const neutral = blok.marker === "dot";

  return (
    <section
      {...storyblokEditable(blok)}
      style={surface ? { backgroundColor: surface.backgroundColor } : undefined}
      className={cn(
        "px-5 md:px-10 lg:px-20",
        sectionSpacing(blok.spacing),
        surface ? "" : "bg-white",
      )}
    >
      <div
        className={cn(
          "mx-auto flex w-full max-w-7xl flex-col gap-8 lg:gap-12",
          image && "lg:flex-row lg:items-stretch lg:gap-14",
        )}
      >
        {image && (
          <div
            className={cn(
              "relative order-2 h-65 w-full shrink-0 overflow-hidden rounded-2xl md:h-84 lg:my-10 lg:h-auto lg:min-h-100 lg:w-[460px]",
              imageLeft ? "lg:order-1" : "lg:order-2",
            )}
          >
            <AssetImage
              src={image}
              focus={blok.image?.focus}
              alt={blok.image?.alt || ""}
              fill
              sizes="(min-width: 1115px) 460px, 100vw"
              className="object-cover"
            />
          </div>
        )}

        <div
          className={cn(
            "order-1 flex flex-1 flex-col gap-8 lg:gap-12",
            image && (imageLeft ? "lg:order-2" : "lg:order-1"),
          )}
        >
          {(blok.heading || blok.intro) && (
            <div className="flex flex-col gap-5">
              {blok.heading && (
                <h2 className={cn("text-section", headingOn(surface, "text-teal-07"))}>
                  {blok.heading}
                </h2>
              )}
              {blok.intro && (
                <p
                  className={cn(
                    "max-w-[943px] text-base leading-relaxed tracking-[-0.01em] lg:text-xl",
                    onSurface(surface, "text-white/85", "text-grey-08", "text-grey-08"),
                  )}
                >
                  {blok.intro}
                </p>
              )}
            </div>
          )}

          <ul className={cn("grid gap-3 lg:gap-4", cols)}>
            {items.map((item) => (
              <li
                key={item}
                style={card ? { backgroundColor: card.backgroundColor } : undefined}
                className={cn(
                  "flex items-start gap-3 rounded-2xl p-4 lg:p-5",
                  card ? "" : "border-grey-01 border",
                )}
              >
                <span
                  className={cn(
                    "flex h-[26px] shrink-0 items-center",
                    neutral ? "text-teal-04" : "text-teal-06",
                  )}
                >
                  {neutral ? <Dot /> : <Tick />}
                </span>
                <span
                  className={cn(
                    "text-base leading-relaxed",
                    onSurface(card ?? surface, "text-white/85", "text-grey-08", "text-grey-08"),
                  )}
                >
                  {item}
                </span>
              </li>
            ))}
          </ul>

          {blok.footnote && (
            <p
              className={cn(
                "max-w-[943px] text-base leading-relaxed",
                onSurface(surface, "text-white/70", "text-grey-06", "text-grey-06"),
              )}
            >
              {blok.footnote}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
